import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "../lib/i18n";
import type { SupportedLanguage } from "../locales";

interface LanguageSwitcherProps {
  compact?: boolean;
}

export default function LanguageSwitcher({ compact = false }: LanguageSwitcherProps) {
  const { language, setLanguage, currentMeta, supportedLanguages, isEurope } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (code: SupportedLanguage) => {
    setLanguage(code);
    setIsOpen(false);
  };

  if (!isEurope) {
    return null;
  }

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select language"
        className={`flex items-center gap-1.5 font-bold font-mono text-xs rounded-lg border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 shadow-sm transition-all cursor-pointer ${
          compact ? "px-2.5 py-1.5" : "px-3 py-2"
        }`}
      >
        <span className="text-sm leading-none">{currentMeta.flag}</span>
        <span className="uppercase tracking-wider">{currentMeta.code}</span>
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-200 text-slate-600 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1 overflow-hidden">
          <div className="px-3 py-1.5 text-[10px] font-mono font-bold uppercase text-slate-400 border-b border-slate-100 tracking-wider">
            Language / Språk
          </div>
          <div role="listbox" className="py-1">
            {supportedLanguages.map((lang) => {
              const isSelected = language === lang.code;
              return (
                <button
                  key={lang.code}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(lang.code)}
                  className={`w-full text-left px-3 py-2 flex items-center justify-between text-xs font-semibold transition-colors cursor-pointer ${
                    isSelected
                      ? "bg-blue-50 text-blue-700"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-base leading-none">{lang.flag}</span>
                    <div className="flex flex-col">
                      <span className="font-bold">{lang.nativeName}</span>
                      <span className="text-[10px] text-slate-500 font-normal">
                        {lang.country}
                      </span>
                    </div>
                  </div>
                  {isSelected && (
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
